/**
 * Order Management Component - Admin Dashboard
 * Allows admins to view and manage all orders
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Search,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  MapPin,
  Phone,
  Calendar,
  X,
  Download,
  Loader2,
  ClipboardList,
  UserPlus,
  Pencil,
  Check,
  AlertTriangle,
} from 'lucide-react';
import orderService from '../../services/orderService';
import deliveryAgentService from '../../services/deliveryAgentService';
import deliveryService from '../../services/deliveryService';
import deliverySlotService from '../../services/deliverySlotService';
import ScheduledListAlerts from './ScheduledListAlerts';
import OrderAmendPanel from './OrderAmendPanel';
import { OrderStatus } from '../../lib/constants';
import { formatPaymentMethod, formatMethodShort, PAYMENT_METHODS } from '../../lib/paymentLabels';

const MAX_PER_AGENT = 50;

// Payment wording lives in lib/paymentLabels so the admin screens and the
// customer's order history cannot describe the same payment differently.

// Payment-status labels that don't render cleanly from the raw enum value.
const PAYMENT_STATUS_LABELS = {
  not_applicable: 'Not Applicable',
  partial_refund: 'Partial Refund'
};

const formatPaymentStatus = (status) => PAYMENT_STATUS_LABELS[status] ?? status;

const SLOT_LABELS = {
  morning: 'Morning (7am – 10am)',
  afternoon: 'Afternoon (12pm – 3pm)',
  evening: 'Evening (5pm – 8pm)',
};

const formatSlot = (slot) => SLOT_LABELS[slot] ?? (slot || '—');

// ── Slot filter (Scheduled tab) ─────────────────────────────────────────────
// The "Sort Slots" dropdown lets the admin narrow the scheduled list to one
// delivery date + slot. It mirrors Checkout's slot picker: the same 3-day
// window and per-date slot resolution (deliverySlotService.getSlotsForDate),
// so the options are exactly the slots orders could have been placed into. The
// one difference: today's in-progress slot stays selectable (the customer view
// drops it at order-cutoff), so the admin can still pull up the batch being
// delivered right now.
const SLOT_WINDOW_DAYS = 3;

const toSlotYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const slotDateLabel = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  return d.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
};

// A slot is offered until its window fully ends. For today this keeps the
// in-progress and later slots (even past their order-cutoff, which still hold
// orders) and drops only slots that are already over.
const isSlotEnded = (slot, date) => {
  if (!slot?.endTime) return false;

  const [h, m] = String(slot.endTime).split(':').map(Number);
  const end = new Date(date);

  end.setHours(h || 0, m || 0, 0, 0);

  return Date.now() >= end.getTime();
};

const formatCurrency = (amount) => {
  const num = Number(amount);

  if (Number.isNaN(num)) return '—';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(num);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';

  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

// Pill colour for the payment column. COD stays amber because it is the one
// method that means money is still owed at the door; the prepaid methods are
// told apart by where the money came from, so a coins order no longer wears the
// same blue as a gateway payment and gets mistaken for one at a glance.
//
// Blue means gateway payment specifically, and razorpay claims it explicitly —
// an unrecognised method takes neutral grey instead. A method this build has
// never heard of is not evidence that money reached Razorpay, and colouring it
// as though it were would reintroduce, in the pill, exactly the false claim this
// ticket removed from the text. Grey reads as "unknown" and pairs with
// formatMethodShort, which shows such a method's raw value for the same reason.
//
// The classes are written out in full because Tailwind scans for literal class
// names — an interpolated `bg-${tone}-100` would be purged from the build.
const getMethodColor = (method) => {
  switch (method) {
    case PAYMENT_METHODS.COD:
      return 'bg-amber-100 text-amber-700';
    case PAYMENT_METHODS.WALLET:
      return 'bg-emerald-100 text-emerald-700';
    case PAYMENT_METHODS.WALLET_RAZORPAY:
      return 'bg-violet-100 text-violet-700';
    case PAYMENT_METHODS.RAZORPAY:
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'confirmed':
      return 'bg-teal-100 text-teal-700';
    case 'processing':
      return 'bg-blue-100 text-blue-700';
    case 'shipped':
      return 'bg-purple-100 text-purple-700';
    case 'delivered':
      return 'bg-green-100 text-green-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const OrderManagement = ({ newCount = 0 }) => {
  const [orders, setOrders] = useState([]);
  const [highlightActive, setHighlightActive] = useState(false);

  useEffect(() => {
    if (newCount > 0) {
      setHighlightActive(true);

      const t = setTimeout(() => setHighlightActive(false), 3000);

      return () => clearTimeout(t);
    }
  }, [newCount]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('on_demand');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState('');

  // Pincode filter: '' = All. Options are the currently-active delivery pincodes
  // for the active tab's order type (on_demand and scheduled have independent sets).
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [pincodeOptions, setPincodeOptions] = useState([]);

  // Scheduled tab only. slotFilter is '' (None) or the index into slotOptions of
  // the selected date+slot; a selected slot narrows the list to that batch and
  // takes over the delivery-date filter (it carries its own date). BMF-93.
  const [slotFilter, setSlotFilter] = useState('');
  const [slotOptions, setSlotOptions] = useState([]);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [downloadingList, setDownloadingList] = useState(null);

  // Scheduled picking-list download state.
  const [downloading, setDownloading] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [downloadingSelectedLists, setDownloadingSelectedLists] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  const ORDERS_PER_PAGE = 15;

  // ── Inline agent assignment state ──────────────────────────────────────────
  const [agents, setAgents] = useState([]);
  const [agentLoads, setAgentLoads] = useState({});
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [loadingAgentList, setLoadingAgentList] = useState(false);
  const [assigningOrderId, setAssigningOrderId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [savingAssign, setSavingAssign] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    revenue: 0
  });

  useEffect(() => {
    setCurrentPage(1);
    setSelectedOrderIds([]);

    fetchOrders(1);
    fetchStats();
  }, [
    search,
    statusFilter,
    deliveryDateFilter,
    slotFilter,
    pincodeFilter,
    activeTab
  ]);

  useEffect(() => {
    setSelectedOrderIds([]);
    fetchOrders(currentPage);
  }, [currentPage]);

  // Slot filtering only applies to scheduled orders — on-demand orders all carry
  // the 'On-Demand' placeholder slot. Clear the selection when leaving that tab
  // so the On-Demand list can't get silently filtered by a slot it can't show.
  useEffect(() => {
    if (activeTab !== 'scheduled' && slotFilter !== '') {
      setSlotFilter('');
    }
  }, [activeTab, slotFilter]);

  // Build the "Sort Slots" options for the Scheduled tab: for each day in the
  // next SLOT_WINDOW_DAYS, the slots resolvable for that date.
  useEffect(() => {
    if (activeTab !== 'scheduled') {
      setSlotOptions([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const opts = [];

      for (let i = 0; i < SLOT_WINDOW_DAYS; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);

        const ymd = toSlotYMD(d);
        const res = await deliverySlotService.getSlotsForDate(ymd);

        if (cancelled) return;

        const slots = res.success ? res.data : [];

        for (const slot of slots) {
          if (i === 0 && isSlotEnded(slot, d)) continue;

          opts.push({
            ymd,
            label: slot.label,
            optionLabel: `${slotDateLabel(d)} — ${slot.snapshot}`
          });
        }
      }

      if (!cancelled) {
        setSlotOptions(opts);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // If the selected slot fell out of a refreshed options list, drop back to None.
  useEffect(() => {
    if (slotFilter !== '' && !slotOptions[Number(slotFilter)]) {
      setSlotFilter('');
    }
  }, [slotOptions, slotFilter]);

  // On-demand and scheduled have independent sets of deliverable pincodes.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const result = await deliveryService.getActivePincodes(activeTab);

      if (cancelled) return;

      const rows = result.success ? result.data : [];

      const seen = new Set();
      const distinct = [];

      for (const row of rows) {
        if (!seen.has(row.pincode)) {
          seen.add(row.pincode);
          distinct.push(row.pincode);
        }
      }

      setPincodeOptions(distinct);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const fetchOrders = async (page = currentPage) => {
    setLoading(true);

    // A selected slot owns the delivery date.
    const selectedSlot =
      activeTab === 'scheduled' && slotFilter !== ''
        ? slotOptions[Number(slotFilter)]
        : null;

    const result = await orderService.getAllOrders({
      search,
      status: statusFilter,
      deliveryDate: selectedSlot ? selectedSlot.ymd : deliveryDateFilter,
      deliveryType: activeTab,
      pincode: pincodeFilter,
      deliverySlot: selectedSlot ? selectedSlot.label : '',
      page,
      limit: ORDERS_PER_PAGE
    });

    if (result.success) {
      setOrders(result.data.orders);
      setTotalPages(result.data.pagination.pages);
      setTotalOrders(result.data.pagination.total);
    }

    setLoading(false);
  };

  const fetchStats = async () => {
    const result = await orderService.getStats();

    if (result.success) {
      setStats(result.data);
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    setDownloadingInvoice(orderId);

    const result = await orderService.downloadInvoice(orderId);

    if (!result.success) {
      alert(result.error || 'Failed to download invoice');
    }

    setDownloadingInvoice(null);
  };

  // Individual picking-list download remains available for on-demand orders.
  const handleDownloadList = async (order) => {
    setDownloadingList(order.id);

    const result = await orderService.downloadPickingList(
      order.id,
      order.orderNumber
    );

    if (!result.success) {
      alert(result.error || 'Failed to download list');
    }

    setDownloadingList(null);
  };

  // Select / deselect an individual scheduled order.
  const handleOrderSelection = (orderId, checked) => {
    setSelectedOrderIds((prev) => {
      if (checked) {
        return prev.includes(orderId)
          ? prev
          : [...prev, orderId];
      }

      return prev.filter((id) => id !== orderId);
    });
  };

  const isOrderSelectionDisabled = (order) =>
  order.status === 'delivered' || order.status === 'cancelled';

  // Select / deselect all scheduled orders visible on the current page.
  const handleSelectAllCurrentPage = (checked) => {
    const scheduledOrderIds = orders
      .filter(
        (order) =>
          order.deliveryType === 'scheduled' && !isOrderSelectionDisabled(order)
      )
      .map((order) => order.orderNumber);

    if (checked) {
      setSelectedOrderIds((prev) => [
        ...new Set([...prev, ...scheduledOrderIds]),
      ]);
    } else {
      setSelectedOrderIds((prev) =>
        prev.filter((id) => !scheduledOrderIds.includes(id))
      );
    }
  };

  // Bulk scheduled picking-list download.
  // The selected order IDs are passed to handleDownloadScheduledList.
  const handleDownloadSelectedLists = async () => {
    if (
      activeTab !== 'scheduled' ||
      selectedOrderIds.length === 0 ||
      downloadingSelectedLists
    ) {
      return;
    }

    setDownloadingSelectedLists(true);

    try {
      await handleDownloadScheduledList({ orderIds: selectedOrderIds });
    } finally {
      setDownloadingSelectedLists(false);
    }
  };

  // Scheduled picking-list download.
  //
  // When orderIds are supplied, the selected scheduled orders are downloaded.
  // The existing item-based download remains supported for ScheduledListAlerts.
  const handleDownloadScheduledList = async ({ orderIds = [] }) => {
    const selectedOrderIds = Array.isArray(orderIds) ? orderIds : [];

    if (selectedOrderIds.length <= 0) {
      alert('Select order(s) to download the list.');
      return;
    }

    const key = selectedOrderIds.length > 0 ? `orders_${selectedOrderIds.join('_')}` : 'scheduled';
    //alert(`key is: ${key}`);
    setDownloading(key);

    try {
      let res = await orderService.downloadScheduledList({ label: `selected orders`, orderIds: selectedOrderIds });

      if (!res.success) {
        alert(res.error || 'Failed to download list');
        return;
      }

      // Clear selection only after a successful bulk download.
      if (selectedOrderIds.length > 0) {
        setSelectedOrderIds([]);
      }
    } finally {
      setDownloading(null);
    }
  };

  const [statusConfirmModal, setStatusConfirmModal] = useState(null);

  const handleStatusUpdate = (orderId, newStatus) => {
    if (newStatus === 'delivered' || newStatus === 'cancelled') {
      setStatusConfirmModal({
        orderId,
        targetStatus: newStatus
      });
    } else {
      executeStatusUpdate(orderId, newStatus);
    }
  };

  const executeStatusUpdate = async (orderId, newStatus) => {
    const result = await orderService.updateOrderStatus(orderId, newStatus);

    if (result.success) {
      fetchOrders();
      fetchStats();
    }
  };

  const handleForceDeliver = async (order) => {
    const reason = window.prompt(
      `Force-deliver order ${order.orderNumber || '#' + order.id}?\n\n` +
      `This bypasses OTP verification. Use only when the customer can't relay the OTP ` +
      `or the agent is locked out after 5 wrong attempts.\n\nReason (logged for audit):`,
      '',
    );

    if (reason === null) return;

    const result = await orderService.forceDeliver(order.id, reason);

    if (result.success) {
      fetchOrders();
      fetchStats();
      window.alert('Order marked as delivered.');
    } else {
      window.alert(result.error || 'Force-deliver failed');
    }
  };

  // ── Agent assignment handlers ───────────────────────────────────────────────

  const fetchAgentsIfNeeded = useCallback(async () => {
    if (agentsLoaded) return;

    setLoadingAgentList(true);

    const res = await deliveryAgentService.getAll();

    if (res.success) {
      const active = (res.data || []).filter(
        (a) => a.status === 'active'
      );

      setAgents(active);

      const loadResults = await Promise.all(
        active.map((a) =>
          deliveryAgentService
            .getAgentLoad(a.id)
            .then((r) => ({
              id: a.id,
              load: r.success ? r.data : null
            }))
        )
      );

      const loadsMap = {};

      loadResults.forEach(({ id, load }) => {
        if (load) {
          loadsMap[id] = load;
        }
      });

      setAgentLoads(loadsMap);
      setAgentsLoaded(true);
    }

    setLoadingAgentList(false);
  }, [agentsLoaded]);

  const openAssignPicker = async (orderId) => {
    await fetchAgentsIfNeeded();
    setAssigningOrderId(orderId);
    setSelectedAgentId('');
  };

  const openReassignPicker = async (orderId, currentAgentId) => {
    await fetchAgentsIfNeeded();
    setAssigningOrderId(orderId);
    setSelectedAgentId(String(currentAgentId));
  };

  const closeAssignPicker = () => {
    setAssigningOrderId(null);
    setSelectedAgentId('');
  };

  const handleSaveAssign = async (order) => {
    if (!selectedAgentId) return;

    setSavingAssign(true);

    const agentId = Number(selectedAgentId);
    const isReassign = !!order.assignedAgent;

    const res = isReassign
      ? await deliveryAgentService.reassignOrder(order.id, agentId)
      : await deliveryAgentService.assignOrder(order.id, agentId);

    if (res.success) {
      const agent = agents.find((a) => a.id === agentId);

      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? {
                ...o,
                assignedAgent: {
                  agentId,
                  agentName: agent?.name || '',
                  agentPhone: agent?.phone || ''
                }
              }
            : o
        )
      );

      const loadRes = await deliveryAgentService.getAgentLoad(agentId);

      if (loadRes.success) {
        setAgentLoads((prev) => ({
          ...prev,
          [agentId]: loadRes.data
        }));
      }

      closeAssignPicker();
    } else {
      alert(res.error || 'Failed to assign agent');
    }

    setSavingAssign(false);
  };

  const renderAgentCell = (order) => {
    const isTerminal =
      order.status === 'delivered' ||
      order.status === 'cancelled';

    const isPickerOpen = assigningOrderId === order.id;

    if (isPickerOpen) {
      return (
        <div className="relative w-32">
          {loadingAgentList ? (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          ) : (
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-32 px-1.5 py-1 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              autoFocus
            >
              <option value="">— Select agent —</option>

              {agents.map((a) => {
                const load = agentLoads[a.id];
                const used = load ? load.used : 0;
                const full = used >= MAX_PER_AGENT;

                return (
                  <option key={a.id} value={a.id} disabled={full}>
                    {a.name} — {used}/{MAX_PER_AGENT}
                    {full ? ' (full)' : ''}
                  </option>
                );
              })}
            </select>
          )}

          <button
            onClick={() => handleSaveAssign(order)}
            disabled={!selectedAgentId || savingAssign}
            className="absolute left-full top-1/2 -translate-y-1/2 ml-1 p-1 rounded hover:bg-green-50 disabled:opacity-40"
            title="Confirm"
          >
            {savingAssign ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#548B2E]" />
            ) : (
              <Check className="w-3.5 h-3.5 text-[#548B2E]" />
            )}
          </button>
        </div>
      );
    }

    if (order.assignedAgent) {
      return (
        <div className="w-32 flex items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-800 truncate">
              {order.assignedAgent.agentName}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {order.assignedAgent.agentPhone}
            </p>
          </div>

          {!isTerminal && (
            <button
              onClick={() =>
                openReassignPicker(
                  order.id,
                  order.assignedAgent.agentId
                )
              }
              className="flex-shrink-0 p-1 rounded hover:bg-gray-100"
              title="Reassign agent"
            >
              <Pencil className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      );
    }

    if (isTerminal) {
      return (
        <div className="w-32">
          <span className="text-xs text-gray-400">
            Not assigned
          </span>
        </div>
      );
    }

    return (
      <div className="w-32">
        <button
          onClick={() => openAssignPicker(order.id)}
          className="inline-flex items-center justify-center gap-1 w-32 py-1 rounded-lg text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:border-[#548B2E] hover:text-[#548B2E] hover:bg-green-50 transition-colors whitespace-nowrap"
        >
          <UserPlus className="w-3 h-3" />
          Assign Agent
        </button>
      </div>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'confirmed':
        return <CheckCircle className="w-4 h-4" />;
      case 'processing':
        return <Package className="w-4 h-4" />;
      case 'shipped':
        return <Truck className="w-4 h-4" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getPaymentColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'refunded':
        return 'text-blue-600';
      case 'partial_refund':
        return 'text-amber-600';
      case 'failed':
        return 'text-red-600';
      case 'not_applicable':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  };

  const scheduledOrdersOnPage = orders.filter(
    (order) => order.deliveryType === 'scheduled' && !isOrderSelectionDisabled(order)
  );

  const selectedScheduledCountOnPage = scheduledOrdersOnPage.filter(
    (order) => selectedOrderIds.includes(order.orderNumber)
  ).length;

  const allScheduledOrdersOnPageSelected =
    scheduledOrdersOnPage.length > 0 &&
    selectedScheduledCountOnPage === scheduledOrdersOnPage.length;

  const someScheduledOrdersOnPageSelected =
    selectedScheduledCountOnPage > 0 &&
    selectedScheduledCountOnPage < scheduledOrdersOnPage.length;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">
          Order Management
        </h1>
        <p className="text-gray-600">
          View and manage customer orders
        </p>
      </div>

      {/* Scheduled slot pick-lists ready to download */}
      <ScheduledListAlerts />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-gray-800">
            {stats.total}
          </p>
          <p className="text-gray-600 text-sm">Total</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-yellow-600">
            {stats.pending}
          </p>
          <p className="text-gray-600 text-sm">Pending</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-teal-600">
            {stats.confirmed}
          </p>
          <p className="text-gray-600 text-sm">Confirmed</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-blue-600">
            {stats.processing}
          </p>
          <p className="text-gray-600 text-sm">Processing</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-purple-600">
            {stats.shipped}
          </p>
          <p className="text-gray-600 text-sm">Shipped</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-green-600">
            {stats.delivered}
          </p>
          <p className="text-gray-600 text-sm">Delivered</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-2xl font-bold text-red-600">
            {stats.cancelled}
          </p>
          <p className="text-gray-600 text-sm">Cancelled</p>
        </div>
      </div>

      {/* Order Type Tabs + Scheduled Bulk Download */}
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
          {[
            {
              id: 'on_demand',
              label: 'On-Demand / Instant'
            },
            {
              id: 'scheduled',
              label: 'Scheduled'
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setPincodeFilter('');
                setSelectedOrderIds([]);
              }}
              className={`px-5 sm:px-7 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#548B2E] text-white shadow'
                  : 'text-gray-600 hover:text-[#2e4e18] hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Only visible for Scheduled orders */}
        {activeTab === 'scheduled' && (
          <button
            type="button"
            onClick={handleDownloadSelectedLists}
            disabled={
              selectedOrderIds.length === 0 ||
              downloadingSelectedLists
            }
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#548B2E] text-white hover:bg-[#437024] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={
              selectedOrderIds.length === 0
                ? 'Select one or more scheduled orders'
                : `Download ${selectedOrderIds.length} selected order${selectedOrderIds.length > 1 ? 's' : ''}`
            }
          >
            {downloadingSelectedLists ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}

            Download Selected Lists

            {selectedOrderIds.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-white/20 text-xs font-semibold">
                {selectedOrderIds.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-4 flex-wrap">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

            <input
              type="text"
              placeholder="Search by order ID, customer name, mobile or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={pincodeFilter}
            onChange={(e) => setPincodeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            title="Filter by deliverable pincode"
          >
            <option value="">All Pincodes</option>

            {pincodeOptions.map((pc) => (
              <option key={pc} value={pc}>
                {pc}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

              <input
                type="date"
                value={deliveryDateFilter}
                onChange={(e) => setDeliveryDateFilter(e.target.value)}
                disabled={
                  activeTab === 'scheduled' &&
                  slotFilter !== ''
                }
                className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] text-sm text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                title={
                  activeTab === 'scheduled' && slotFilter !== ''
                    ? 'Cleared while a slot is selected'
                    : 'Filter by scheduled delivery date'
                }
              />
            </div>

            {deliveryDateFilter &&
              !(activeTab === 'scheduled' && slotFilter !== '') && (
                <button
                  onClick={() => setDeliveryDateFilter('')}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                  title="Clear date filter"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
          </div>

          {/* Slot filter — Scheduled tab only */}
          {activeTab === 'scheduled' && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="order-slot"
                className="text-sm text-gray-500 whitespace-nowrap"
              >
                Sort Slots
              </label>

              <select
                id="order-slot"
                value={slotFilter}
                onChange={(e) => setSlotFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] text-sm text-gray-700"
                title="Filter the scheduled list to a delivery slot"
              >
                <option value="">None</option>

                {slotOptions.map((o, i) => (
                  <option key={i} value={i}>
                    {o.optionLabel}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#548B2E] mx-auto"></div>
            <p className="text-gray-500 mt-2">
              Loading orders...
            </p>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">
              No orders found
            </p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[calc(100vh-320px)]">
            <table className="w-full min-w-[1120px]">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    <div className="flex items-center justify-left gap-2">
                      {activeTab === 'scheduled' && (
                        <input
                          type="checkbox"
                          checked={allScheduledOrdersOnPageSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                someScheduledOrdersOnPageSelected;
                            }
                          }}
                          onChange={(e) =>
                            handleSelectAllCurrentPage(
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 rounded border-gray-300 text-[#548B2E] focus:ring-[#548B2E] cursor-pointer"
                          aria-label="Select all scheduled orders on this page"
                          title="Select all scheduled orders on this page"
                        />
                      )}
                      <span>Order ID</span>
                    </div>
                  </th>

                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">List / Slot</th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Customer
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Pincode
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Items
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Total
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Method
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Payment
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Status
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Delivery Agent
                  </th>

                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Date
                  </th>

                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {orders.map((order, index) => {
                //const isOrderSelectionDisabled = order.status === 'delivered' || order.status === 'cancelled';
                const disableOrderSelection = isOrderSelectionDisabled(order);

                return (
                  <tr
                    key={order.orderNumber}
                    className={`hover:bg-gray-50 transition-colors ${
                      highlightActive && index < newCount
                        ? 'bg-orange-50 border-l-4 border-orange-400'
                        : ''
                    }`}
                  >
                    <td className="px-4 py-4">
                      <div className="inline-flex items-center gap-2">
                        {/* Scheduled order selection checkbox */}
                        {order.deliveryType === 'scheduled' && (
                          <label
                            className={`inline-flex items-center justify-center p-1 rounded-lg ${
                              disableOrderSelection
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:bg-green-50 cursor-pointer'
                            }`}
                            title={
                              disableOrderSelection
                                ? order.status.charAt(0).toUpperCase() + order.status.slice(1) + ' orders cannot be selected!'
                                : 'Select order to download'
                            }
                          >
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.includes(order.orderNumber)}
                              onChange={(e) =>
                                handleOrderSelection(
                                  order.orderNumber,
                                  e.target.checked
                                )
                              }
                              disabled={disableOrderSelection}
                              className={`w-4 h-4 rounded border-gray-300 text-[#548B2E] focus:ring-[#548B2E] ${
                                disableOrderSelection
                                  ? 'cursor-not-allowed'
                                  : 'cursor-pointer'
                              }`}
                              aria-label={`Select order ${
                                order.orderNumber || order.id
                              }`}
                            />
                          </label>
                        )}

                        {/* Order ID */}
                        <span className="font-mono text-sm font-medium text-gray-800">
                          {order.orderNumber || `#${order.id}`}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-4 text-center">
                      {order.deliveryType === 'scheduled' ? (
                        <div className="inline-flex items-center gap-1.5">
                          {/* Delivery date / slot */}
                          <span className="inline-flex flex-col items-center gap-0.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap">
                            {order.deliveryDate && (
                              <span className="text-[11px] font-semibold">
                                {new Date(
                                  order.deliveryDate
                                ).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  timeZone: 'Asia/Kolkata'
                                })}
                              </span>
                            )}

                            <span className="inline-flex items-center gap-1.5">
                              🗓 {formatSlot(order.deliverySlot)}
                            </span>
                          </span>
                        </div>
                      ) : order.status === 'delivered' ? (
                        // Once an on-demand order is delivered there's nothing left to pack.
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 whitespace-nowrap">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Delivered
                        </span>
                      ) : (
                        <button
                          onClick={() =>
                            handleDownloadList(order)
                          }
                          disabled={
                            downloadingList === order.id
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[#548B2E] text-[#548B2E] hover:bg-green-50 disabled:opacity-50 whitespace-nowrap"
                          title="Download picking list (A4, printable)"
                        >
                          {downloadingList === order.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <ClipboardList className="w-3.5 h-3.5" />
                          )}

                          Download List
                        </button>
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <div className="max-w-[160px]">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-gray-800 truncate">
                            {order.userName}
                          </p>

                          {order.isGuest && (
                            <span className="flex-shrink-0 text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                              Guest
                            </span>
                          )}
                        </div>

                        {order.userEmail && (
                          <p className="text-sm text-gray-500 truncate">
                            {order.userEmail}
                          </p>
                        )}

                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          {order.shippingAddress?.phone ||
                            order.userPhone ||
                            '—'}
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        {order.shippingAddress?.pincode || '—'}
                      </p>
                    </td>

                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-700 max-w-[150px] truncate">
                        {order.items.length <= 2
                          ? order.items.map((i) => i.name).join(', ')
                          : order.items
                              .slice(0, 2)
                              .map((i) => i.name)
                              .join(', ') + '...'}
                      </div>
                    </td>

                    <td className="px-4 py-4 font-medium text-gray-800 whitespace-nowrap">
                      {formatCurrency(order.total)}
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getMethodColor(
                          order.paymentMethod
                        )}`}
                      >
                        {formatMethodShort(order)}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`text-sm font-medium capitalize ${getPaymentColor(
                          order.paymentStatus
                        )}`}
                      >
                        {formatPaymentStatus(
                          order.paymentStatus
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {getStatusIcon(order.status)}
                        {order.status}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      {renderAgentCell(order)}
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(order.createdAt)}
                    </td>

                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() =>
                            handleDownloadInvoice(order.id)
                          }
                          disabled={
                            downloadingInvoice === order.id
                          }
                          className="p-1.5 hover:bg-green-50 rounded-lg disabled:opacity-50"
                          title="Download Invoice"
                        >
                          {downloadingInvoice === order.id ? (
                            <Loader2 className="w-4 h-4 text-[#548B2E] animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 text-[#548B2E]" />
                          )}
                        </button>

                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>

                        {order.status !== 'delivered' &&
                          order.status !== 'cancelled' && (
                            <select
                              value={order.status}
                              onChange={(e) =>
                                handleStatusUpdate(
                                  order.id,
                                  e.target.value
                                )
                              }
                              className="text-xs border border-gray-200 rounded-lg px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                            >
                              <option value="pending">
                                Pending
                              </option>
                              <option value="processing">
                                Processing
                              </option>
                              <option value="shipped">
                                Shipped
                              </option>
                              <option value="delivered">
                                Delivered
                              </option>
                              <option value="cancelled">
                                Cancelled
                              </option>
                            </select>
                          )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-white rounded-xl shadow-sm mt-2">
          <p className="text-sm text-gray-500">
            Showing{' '}
            {((currentPage - 1) * ORDERS_PER_PAGE) + 1}–
            {Math.min(
              currentPage * ORDERS_PER_PAGE,
              totalOrders
            )}{' '}
            of {totalOrders} orders
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() =>
                setCurrentPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, i) => i + 1
            )
              .filter(
                (p) =>
                  p === 1 ||
                  p === totalPages ||
                  Math.abs(p - currentPage) <= 1
              )
              .reduce((acc, p, idx, arr) => {
                if (
                  idx > 0 &&
                  p - arr[idx - 1] > 1
                ) {
                  acc.push('...');
                }

                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-2 text-gray-400"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      currentPage === p
                        ? 'bg-[#548B2E] text-white border-[#548B2E]'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

            <button
              onClick={() =>
                setCurrentPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      {statusConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-center transform transition-all scale-100">
            <div
              className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4 ${
                statusConfirmModal.targetStatus === 'cancelled'
                  ? 'bg-red-100 text-red-600'
                  : 'bg-green-100 text-green-600'
              }`}
            >
              <AlertTriangle className="w-6 h-6" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Status Change
            </h3>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to mark this order as{' '}
              <span
                className={`font-semibold capitalize ${
                  statusConfirmModal.targetStatus === 'cancelled'
                    ? 'text-red-600'
                    : 'text-green-600'
                }`}
              >
                {statusConfirmModal.targetStatus}
              </span>
              ? This action will update the order timeline.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setStatusConfirmModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={async () => {
                  const {
                    orderId,
                    targetStatus
                  } = statusConfirmModal;

                  setStatusConfirmModal(null);

                  await executeStatusUpdate(
                    orderId,
                    targetStatus
                  );
                }}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  statusConfirmModal.targetStatus === 'cancelled'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-[#548B2E] hover:bg-[#437024]'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
          onForceDeliver={handleForceDeliver}
          onDownloadInvoice={handleDownloadInvoice}
          downloadingInvoice={downloadingInvoice}
          onAmended={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
};

// Assigned Agent Info — fetched fresh when modal opens
const AssignedAgentInfo = ({ orderId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    import('../../services/deliveryAgentService').then((m) => {
      m.default.getOrderAssignment(orderId).then((r) => {
        if (r.success) {
          setData(r.data);
        }

        setLoading(false);
      });
    });
  }, [orderId]);

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-2 text-gray-400 text-sm">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-[#548B2E] rounded-full animate-spin" />
        Loading agent info...
      </div>
    );
  }

  if (!data?.current) {
    return (
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
        <p className="text-sm text-amber-700 font-medium">
          No agent assigned yet
        </p>
        <p className="text-xs text-amber-600 mt-1">
          Agent will be auto-assigned when order is processed
        </p>
      </div>
    );
  }

  const { current, history } = data;

  const fmt = (d) => {
    if (!d) return '—';

    return new Date(d).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
        <span>Assigned Agent</span>

        {history?.length > 0 && (
          <button
            onClick={() => setShowHistory((h) => !h)}
            className="ml-auto text-xs text-blue-600 hover:underline"
          >
            {showHistory ? 'Hide' : 'Show'} history (
            {history.length})
          </button>
        )}
      </h4>

      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
        <div className="w-9 h-9 rounded-full bg-[#f2f8eb] flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-[#548B2E]">
            {current.agentName?.charAt(0).toUpperCase()}
          </span>
        </div>

        <div className="flex-1">
          <p className="font-medium text-gray-800 text-sm">
            {current.agentName}
          </p>

          <p className="text-xs text-gray-500">
            {current.agentPhone} · {current.vehicleType}
          </p>
        </div>

        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            current.availability === 'on_delivery'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-green-100 text-green-700'
          }`}
        >
          {current.availability === 'on_delivery'
            ? 'On Delivery'
            : 'At Warehouse'}
        </span>
      </div>

      <p className="text-xs text-gray-400 mt-2">
        Assigned at {fmt(current.assignedAt)}
      </p>

      {showHistory && history?.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Previous Assignments
          </p>

          {history.map((h) => (
            <div
              key={h.id}
              className="p-2.5 bg-white border border-gray-100 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-gray-700">
                  {h.agentName}
                </p>

                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full capitalize">
                  {h.unassignedReason || 'released'}
                </span>
              </div>

              <p className="text-xs text-gray-400 mt-1">
                {fmt(h.assignedAt)} → {fmt(h.unassignedAt)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Delivery OTP block — read-only chip + Force Deliver escape hatch.
// Rendered only when order.status === 'shipped' && !isGuest && deliveryOtp exists.
const DeliveryOtpBlock = ({ order, onForceDeliver }) => {
  const locked = !!order.deliveryOtpLocked;
  const attempts = order.deliveryOtpAttempts || 0;

  return (
    <div
      className={`rounded-lg p-4 border ${
        locked
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4
            className={`font-medium mb-1 ${
              locked ? 'text-red-800' : 'text-amber-800'
            }`}
          >
            Delivery OTP

            {locked && (
              <span className="ml-2 text-xs font-semibold uppercase tracking-wide">
                Locked
              </span>
            )}
          </h4>

          <p className="font-mono text-2xl font-bold tracking-[0.4em] text-gray-900">
            {order.deliveryOtp}
          </p>

          <p className="text-xs text-gray-600 mt-1">
            {locked
              ? `Agent locked out after ${attempts} wrong attempts. Use Force Deliver to bypass.`
              : 'Share this with the customer if they didn\'t receive the push. Customer reads it to the agent at handover.'}
          </p>

          {attempts > 0 && !locked && (
            <p className="text-xs text-amber-700 mt-1 font-medium">
              {attempts} wrong{' '}
              {attempts === 1 ? 'attempt' : 'attempts'} so far (
              {5 - attempts} remaining)
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onForceDeliver}
          className="shrink-0 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
        >
          Force Deliver
        </button>
      </div>
    </div>
  );
};

// Order Detail Modal
const OrderDetailModal = ({
  order,
  onClose,
  onForceDeliver,
  onDownloadInvoice,
  downloadingInvoice,
  onAmended
}) => {
  const handleForceDeliver = () =>
    onForceDeliver?.(order);

  const amendable =
    order.status !== OrderStatus.DELIVERED &&
    order.status !== OrderStatus.CANCELLED;

  const hasPendingAmendment =
    order.amendmentStatus === 'PENDING_CUSTOMER_APPROVAL';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Order Details
            </h3>

            <p className="text-sm text-gray-500 font-mono">
              {order.id}
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Status & Date */}
          <div className="flex items-center justify-between">
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                order.status
              )}`}
            >
              {order.status}
            </span>

            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(order.createdAt)}
            </span>
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-medium text-gray-800">
                Customer
              </h4>

              {order.isGuest && (
                <span className="text-[11px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  Guest Order
                </span>
              )}
            </div>

            <p className="text-gray-700 font-medium">
              {order.userName}
            </p>

            {order.userEmail && (
              <p className="text-sm text-gray-500">
                {order.userEmail}
              </p>
            )}

            {order.userPhone && (
              <p className="text-sm text-gray-500">
                📞 {order.userPhone}
              </p>
            )}

            {!order.userEmail && !order.userPhone && (
              <p className="text-sm text-gray-400 italic">
                No contact info provided
              </p>
            )}
          </div>

          {/* Order Note */}
          {order.orderNote && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 mb-1 flex items-center gap-2">
                📝 Order Note from Customer
              </h4>

              <p className="text-sm text-amber-900">
                {order.orderNote}
              </p>
            </div>
          )}

          {/* Shipping Address */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Shipping Address
            </h4>

            <p className="text-gray-700">
              {order.shippingAddress.name}
            </p>

            <p className="text-sm text-gray-600">
              {order.shippingAddress.address}
            </p>

            <p className="text-sm text-gray-600">
              {order.shippingAddress.city},{' '}
              {order.shippingAddress.state} -{' '}
              {order.shippingAddress.pincode}
            </p>

            <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
              <Phone className="w-3 h-3" />
              {order.shippingAddress.phone}
            </p>

            {order.shippingAddress.alternatePhone && (
              <p className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                <Phone className="w-3 h-3" />
                {order.shippingAddress.alternatePhone}
                <span className="text-xs text-gray-400 ml-1">
                  (Alt)
                </span>
              </p>
            )}
          </div>

          {/* Order Items */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">
              Items
            </h4>

            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />

                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {item.name}
                      </p>

                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity}
                      </p>
                    </div>

                    <p className="font-medium text-gray-800 flex-shrink-0">
                      {formatCurrency(
                        item.price * item.quantity
                      )}
                    </p>
                  </div>

                  {item.itemNote && (
                    <div className="mt-2 ml-20 flex items-start gap-1.5 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">
                        📝
                      </span>

                      <p className="text-xs text-amber-800">
                        {item.itemNote}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Order Amendment */}
          {hasPendingAmendment ? (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                An amendment is pending customer approval for this order. No new amendment can be sent until the customer responds.
              </div>
            </div>
          ) : amendable ? (
            <OrderAmendPanel
              order={order}
              onDone={() => onAmended?.()}
            />
          ) : null}

          {/* Order Summary */}
          <div className="border-t border-gray-200 pt-4 space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>
                {formatCurrency(order.subtotal)}
              </span>
            </div>

            <div className="flex justify-between text-gray-600">
              <span>Shipping</span>
              <span>
                {order.shipping === 0
                  ? 'Free'
                  : formatCurrency(order.shipping)}
              </span>
            </div>

            <div className="flex justify-between text-gray-600">
              <span>Tax</span>
              <span>
                {formatCurrency(order.tax)}
              </span>
            </div>

            <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t border-gray-200">
              <span>Total</span>
              <span>
                {formatCurrency(order.total)}
              </span>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-800 mb-2">
              Payment
            </h4>

            <div className="flex items-center justify-between">
              <span className="text-gray-600">
                {formatPaymentMethod(order)}
              </span>

              <span
                className={`font-medium capitalize ${
                  order.paymentStatus === 'paid'
                    ? 'text-green-600'
                    : order.paymentStatus === 'refunded'
                      ? 'text-blue-600'
                      : order.paymentStatus === 'partial_refund'
                        ? 'text-amber-600'
                        : order.paymentStatus === 'not_applicable'
                          ? 'text-gray-500'
                          : 'text-yellow-600'
                }`}
              >
                {formatPaymentStatus(
                  order.paymentStatus
                )}
              </span>
            </div>
          </div>

          {/* Delivery Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-800 mb-3">
              Delivery
            </h4>

            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                  order.deliveryType === 'scheduled'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-orange-100 text-orange-700'
                }`}
              >
                {order.deliveryType === 'scheduled'
                  ? '🗓 Scheduled'
                  : '⚡ On-Demand'}
              </span>

              {order.deliveryType === 'scheduled' &&
                order.deliveryDate && (
                  <span className="text-sm text-gray-600">
                    {new Date(
                      order.deliveryDate
                    ).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      timeZone: 'Asia/Kolkata'
                    })}
                  </span>
                )}
            </div>

            {order.deliverySlot && (
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium">
                  Slot:
                </span>{' '}
                {order.deliveryType === 'on_demand'
                  ? 'On-Demand (immediate)'
                  : order.deliverySlot === 'morning'
                    ? 'Morning (7am – 10am)'
                    : order.deliverySlot === 'afternoon'
                      ? 'Afternoon (12pm – 3pm)'
                      : order.deliverySlot === 'evening'
                        ? 'Evening (5pm – 8pm)'
                        : order.deliverySlot}
              </p>
            )}

            {order.deliveryInstruction && (
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium">
                  Drop-off:
                </span>{' '}
                {order.deliveryInstruction === 'main_gate'
                  ? 'Society Main Gate'
                  : 'Doorstep'}
              </p>
            )}
          </div>

          {/* Assigned Agent */}
          <AssignedAgentInfo orderId={order.id} />

          {/* Delivery OTP */}
          {order.status === 'shipped' &&
            !order.isGuest &&
            order.deliveryOtp && (
              <DeliveryOtpBlock
                order={order}
                onForceDeliver={() =>
                  handleForceDeliver(order)
                }
              />
            )}

          {/* Tracking Info */}
          {order.trackingNumber && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-medium text-purple-800 mb-1">
                Tracking Number
              </h4>

              <p className="font-mono text-purple-700">
                {order.trackingNumber}
              </p>
            </div>
          )}

          {/* Timeline */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">
              Timeline
            </h4>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>

                <span className="text-sm text-gray-600">
                  Order placed -{' '}
                  {formatDate(order.createdAt)}
                </span>
              </div>

              {order.updatedAt !== order.createdAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>

                  <span className="text-sm text-gray-600">
                    Last updated -{' '}
                    {formatDate(order.updatedAt)}
                  </span>
                </div>
              )}

              {order.deliveredAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>

                  <span className="text-sm text-gray-600">
                    Delivered -{' '}
                    {formatDate(order.deliveredAt)}
                  </span>
                </div>
              )}

              {order.cancelledAt && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>

                    <span className="text-sm text-gray-600">
                      Cancelled -{' '}
                      {formatDate(order.cancelledAt)}
                    </span>
                  </div>

                  {order.cancelReason && (
                    <div className="ml-5 text-xs text-red-500 font-medium bg-red-50 px-2 py-1 rounded border border-red-100 max-w-md">
                      Reason: "{order.cancelReason}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          {order.status === 'delivered' ? (
            <button
              onClick={() =>
                onDownloadInvoice(order.id)
              }
              disabled={
                downloadingInvoice === order.id
              }
              className="flex items-center gap-2 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] disabled:opacity-50 transition-colors"
            >
              {downloadingInvoice === order.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}

              Download Invoice
            </button>
          ) : (
            <span />
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

OrderManagement.propTypes = {
  newCount: PropTypes.number
};

export default OrderManagement;